/**
 * ReconnectionOverlay - Shows during connection issues
 * Displays attempt counter and manual retry option
 */

import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Loader2, RefreshCw, Phone } from "lucide-react";

export interface ReconnectionOverlayProps {
  isReconnecting: boolean;
  attempt: number;
  maxAttempts: number;
  canManualRetry: boolean;
  lastError: string | null;
  onManualRetry: () => void;
  onContactSupport?: () => void;
  className?: string;
}

export function ReconnectionOverlay({
  isReconnecting,
  attempt,
  maxAttempts,
  canManualRetry,
  lastError,
  onManualRetry,
  onContactSupport,
  className,
}: ReconnectionOverlayProps) {
  if (!isReconnecting && !canManualRetry) {
    return null;
  }

  return (
    <div
      className={cn(
        "absolute inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm",
        className
      )}
    >
      <div className="bg-card rounded-2xl p-8 max-w-md mx-4 text-center shadow-xl">
        {isReconnecting ? (
          <>
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-sage-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              Verbindung wird wiederhergestellt
            </h2>
            <p className="text-muted-foreground mb-4">
              Versuch {attempt} von {maxAttempts}
            </p>
            <p className="text-sm text-muted-foreground">
              Ihr Gespräch wird fortgesetzt, sobald die Verbindung
              wiederhergestellt ist.
            </p>
            {/* Progress bar */}
            <div className="mt-6 w-full bg-muted rounded-full h-2">
              <div
                className="bg-sage-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(attempt / maxAttempts) * 100}%` }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <RefreshCw className="h-6 w-6 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold mb-2">
              Verbindung fehlgeschlagen
            </h2>
            <p className="text-muted-foreground mb-6">
              {lastError ||
                "Die automatische Wiederverbindung war nicht erfolgreich."}
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Ihre Sitzung ist noch aktiv. Sie können manuell einen neuen
              Verbindungsversuch starten.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={onManualRetry}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Erneut verbinden
              </Button>
              {onContactSupport && (
                <Button variant="outline" onClick={onContactSupport}>
                  <Phone className="h-4 w-4 mr-2" />
                  Support
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
